// Generado por la división del monolito. Importa helpers de ./common.jsx.
import { useState, useEffect, useCallback, MONTHS, fmt, Ic } from './common.jsx';

export function ChangeLogPage({activeUser=null}={}) {
  const isSuperAdmin = activeUser?.role === 'super_admin';
  const [log, setLog] = useState([]);
  const [filter, setFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');                  // 3 — buscador
  const [expandedEntry, setExpandedEntry] = useState(null);  // 3 — click → detalle JSON
  // Días colapsables. Por defecto solo el primer día (hoy) está abierto.
  const [openDays, setOpenDays] = useState(() => new Set());
  const toggleDay = (d) => setOpenDays(s => { const n = new Set(s); if (n.has(d)) n.delete(d); else n.add(d); return n; });

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/changelog?limit=500').then(x => x.json());
      setLog(r);
    } catch (e) { setMsg('Error: ' + e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const triggerSync = async () => {
    setSyncing(true); setMsg('Lanzando sincronización…');
    try {
      await fetch('/api/hubspot/auto-sync', { method: 'POST' });
      setMsg('Sincronización completada.');
      await load();
    } catch (e) { setMsg('Error: ' + e.message); }
    setSyncing(false);
  };

  const TYPES = {
    daily_summary: { label: 'Resumen del día',  color: '#0ABB87', icon: '📊' },
    sync_auto:    { label: 'Sync automático',   color: '#165fc6', icon: '🕘' },
    sync_refunds: { label: 'Sync bajas',        color: '#df2020', icon: '⤺' },
    sync_unpaid:  { label: 'Sync impagos',      color: '#08966C', icon: '💳' },
    sync_recobros:{ label: 'Sync recobros',     color: '#08966C', icon: '💳' },
    plan_create:  { label: 'Plan creado',       color: '#117010', icon: '＋' },
    plan_update:  { label: 'Plan editado',      color: '#1a1a1a', icon: '✎' },
    plan_delete:  { label: 'Plan eliminado',    color: '#df2020', icon: '✕' },
    team_create:  { label: 'Equipo creado',     color: '#117010', icon: '＋' },
    team_update:  { label: 'Equipo editado',    color: '#1a1a1a', icon: '✎' },
    team_delete:  { label: 'Equipo eliminado',  color: '#df2020', icon: '✕' },
    level_create: { label: 'Nivel creado',      color: '#117010', icon: '＋' },
    level_update: { label: 'Nivel editado',     color: '#1a1a1a', icon: '✎' },
    badge_create: { label: 'Insignia creada',   color: '#117010', icon: '🏅' },
    badge_update: { label: 'Insignia editada',  color: '#1a1a1a', icon: '🏅' },
    bonus_create: { label: 'Bonus añadido',     color: '#117010', icon: '€' },
    bonus_delete: { label: 'Bonus eliminado',   color: '#df2020', icon: '€' },
    user_edit:    { label: 'Usuario editado',   color: '#7B68EE', icon: '👤' },
    cobros_update:{ label: 'Cobros ajustado',   color: '#1a1a1a', icon: '€' },
    deal_change_owner:     { label: 'Owner cambiado',  color: '#7B68EE', icon: '👤' },
    deal_change_closedate: { label: 'Fecha cierre',    color: '#165fc6', icon: '📅' },
    deal_change_amount:    { label: 'Monto cambiado',  color: '#08966C', icon: '€' },
    deal_change_cuotas:    { label: 'Cuotas cambiadas',color: '#08966C', icon: '#' }
  };

  const filtered = (() => {
    let out = filter === 'all'
      ? log
      : log.filter(e => filter === 'deal_change_owner'
          ? (e.type || '').startsWith('deal_change_')
          : e.type === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(e => (e.summary||'').toLowerCase().includes(q));
    }
    return out;
  })();

  // KPIs arriba: contadores rápidos de actividad reciente. Ayudan a entender
  // de un vistazo cuánto se ha tocado hoy/esta semana/este mes sin tener
  // que scrollear los días.
  const stats = (() => {
    const now = Date.now();
    const todayStart = new Date(); todayStart.setHours(0,0,0,0);
    const weekAgo = now - 7 * 86400000;
    const monthAgo = now - 30 * 86400000;
    let today=0, week=0, month=0;
    for (const e of log) {
      const t = new Date(e.at||0).getTime();
      if (t >= todayStart.getTime()) today++;
      if (t >= weekAgo) week++;
      if (t >= monthAgo) month++;
    }
    return { today, week, month };
  })();

  const groups = {};
  for (const e of filtered) {
    const day = (e.at || '').slice(0, 10);
    if (!groups[day]) groups[day] = [];
    groups[day].push(e);
  }
  const dayKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  // Auto-abrir el día más reciente al cargar (UX: usuario ve actividad
  // reciente sin tener que clickar).
  useEffect(() => {
    if (dayKeys.length > 0 && openDays.size === 0) {
      setOpenDays(new Set([dayKeys[0]]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log.length]);

  const fmtDay = ymd => {
    if (!ymd) return '';
    const today = new Date().toISOString().slice(0, 10);
    const y = new Date(); y.setDate(y.getDate() - 1);
    const yest = y.toISOString().slice(0, 10);
    if (ymd === today) return 'Hoy';
    if (ymd === yest)  return 'Ayer';
    return new Date(ymd).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long' });
  };
  const fmtTime = iso => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  const generateDailySummary = async () => {
    setMsg('Generando resumen del día anterior…');
    try {
      const r = await fetch('/api/changelog/build-daily-summary', { method:'POST' }).then(x => x.json());
      if (r.skipped) setMsg(`Resumen del ${r.day} ya existía o sin cambios.`);
      else setMsg(`✓ Resumen ${r.day} generado.`);
      await load();
    } catch (e) { setMsg('Error: ' + e.message); }
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h2>Log de cambios</h2>
          <p>Auditoría · {log.length} entrada{log.length === 1 ? '' : 's'} (últimas 500)</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <button className="btn btn-gh btn-sm" onClick={generateDailySummary} title="Genera el resumen del día anterior (automático cada día a las 9:00).">
            📊 Resumen del día
          </button>
          {isSuperAdmin && (
            <button className="btn btn-pu btn-sm" onClick={triggerSync} disabled={syncing} title="Solo super admin">
              {syncing ? 'Sincronizando…' : '↻ Sincronizar HubSpot'}
            </button>
          )}
        </div>
      </div>
      <div className="page">
        {msg && <div className="card" style={{ padding: 10, marginBottom: 14, fontSize: 12, color: 'var(--tx-mu)' }}>{msg}</div>}

        {/* KPIs de actividad — vistazo rápido sin scroll. */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginBottom:14}}>
          <div className="card" style={{padding:'12px 14px'}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--tx-mu)',textTransform:'uppercase',letterSpacing:'.5px'}}>Hoy</div>
            <div style={{fontSize:22,fontWeight:700,color:'var(--tx)',marginTop:3,letterSpacing:'-.5px'}}>{stats.today}</div>
            <div style={{fontSize:11,color:'var(--tx-mu)',marginTop:2}}>cambios registrados</div>
          </div>
          <div className="card" style={{padding:'12px 14px'}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--tx-mu)',textTransform:'uppercase',letterSpacing:'.5px'}}>Últimos 7 días</div>
            <div style={{fontSize:22,fontWeight:700,color:'var(--pu)',marginTop:3,letterSpacing:'-.5px'}}>{stats.week}</div>
            <div style={{fontSize:11,color:'var(--tx-mu)',marginTop:2}}>cambios registrados</div>
          </div>
          <div className="card" style={{padding:'12px 14px'}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--tx-mu)',textTransform:'uppercase',letterSpacing:'.5px'}}>Últimos 30 días</div>
            <div style={{fontSize:22,fontWeight:700,color:'var(--tx)',marginTop:3,letterSpacing:'-.5px'}}>{stats.month}</div>
            <div style={{fontSize:11,color:'var(--tx-mu)',marginTop:2}}>cambios registrados</div>
          </div>
        </div>

        {/* Buscador + filtros — toolbar arriba */}
        <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
          <input className="fi" style={{fontSize:13,flex:'1 1 240px',maxWidth:340}}
            placeholder="🔍 Buscar en el log… (nombre comercial, deal, plan…)"
            value={search} onChange={e=>setSearch(e.target.value)}/>
          <select className="fs" style={{fontSize:12,minWidth:180}} value={filter} onChange={e=>setFilter(e.target.value)}>
            {[
              ['all','Todos los tipos'],
              ['daily_summary','Resúmenes diarios'],
              ['sync_auto','Sync automático'],
              ['sync_refunds','Bajas'],
              ['sync_unpaid','Impagos'],
              ['plan_create','Planes'],
              ['team_create','Equipos'],
              ['user_edit','Usuarios'],
              ['bonus_create','Bonus'],
              ['deal_change_owner','Cambios deals'],
            ].map(([k,l]) => {
              const matchFn = k==='deal_change_owner'
                ? (e => e.type && e.type.startsWith('deal_change_'))
                : (e => e.type === k);
              const count = k==='all' ? log.length : log.filter(matchFn).length;
              return <option key={k} value={k}>{l} ({count})</option>;
            })}
          </select>
          <span style={{fontSize:11,color:'var(--tx-mu)',marginLeft:'auto'}}>
            {filtered.length} de {log.length} entradas{search.trim()?` · "${search.trim()}"`:''}
          </span>
        </div>

        {dayKeys.length === 0 && (
          <div className="card" style={{padding:'24px 20px'}}>
            <div className="em"><h3>Sin entradas</h3><p>Pulsa "Sincronizar ahora" para registrar el primer evento.</p></div>
          </div>
        )}

        {/* Cada día = card colapsable. Click en cabecera expande/colapsa. */}
        {dayKeys.map(day => {
          const isOpen = openDays.has(day);
          const entries = groups[day];
          return (
            <div key={day} className="card" style={{padding:0,overflow:'hidden',marginBottom:10}}>
              <div onClick={()=>toggleDay(day)}
                style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',background:'var(--bg)',cursor:'pointer',userSelect:'none',gap:10,flexWrap:'wrap'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{width:14,height:14,color:'var(--tx-mu)',transition:'transform .15s',transform: isOpen?'rotate(90deg)':'rotate(0deg)'}}>
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                  <span style={{fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:'.4px',color:'var(--tx)'}}>{fmtDay(day)}</span>
                  <span style={{fontSize:11,color:'var(--tx-mu)'}}>· {entries.length} cambio{entries.length!==1?'s':''}</span>
                </div>
                {/* Preview de tipos cuando está colapsado */}
                {!isOpen && (
                  <div style={{display:'flex',gap:4}}>
                    {[...new Set(entries.map(e => e.type))].slice(0,6).map(t => {
                      const tm = TYPES[t] || { color: 'var(--tx-mu)', icon: '·' };
                      return <span key={t} title={tm.label||t} style={{fontSize:14,color:tm.color,opacity:.85}}>{tm.icon}</span>;
                    })}
                  </div>
                )}
              </div>
              {isOpen && entries.map((e, i) => {
                const t = TYPES[e.type] || { label: e.type, color: 'var(--tx-mu)', icon: '·' };
                const hsUrl = e.details?.hubspotUrl
                  || (e.details?.dealId ? `https://app-eu1.hubspot.com/contacts/25603833/record/0-3/${e.details.dealId}` : null);
                // Banner especial para los resúmenes diarios (chips con cada delta).
                if (e.type === 'daily_summary' && e.details?.deltas) {
                  const d = e.details.deltas;
                  const chips = [
                    {n:d.sales,         label:'ventas',         color:'#08966C'},
                    {n:d.refunds,       label:'bajas',          color:'#df2020'},
                    {n:d.unpaidFirm,    label:'impagos',        color:'#a06000'},
                    {n:d.usersCreated,  label:'comerciales',    color:'#165fc6'},
                    {n:d.adminChanges,  label:'ajustes admin',  color:'#7B68EE'}
                  ].filter(c => c.n > 0);
                  return (
                    <div key={e.id || i} style={{padding:'14px 18px',borderTop:'1px solid var(--bds)',background:'linear-gradient(180deg,#E7F9F4 0%,var(--wh) 100%)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                        <div style={{fontSize:20}}>📊</div>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:'var(--tx)'}}>Resumen del día</div>
                          <div style={{fontSize:10,color:'var(--tx-mu)'}}>{t.label} · generado a las {fmtTime(e.at)}</div>
                        </div>
                      </div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {chips.map((c,ci) => (
                          <div key={ci} style={{
                            background:'var(--wh)',border:`1px solid ${c.color}33`,
                            borderRadius:8,padding:'8px 12px',display:'flex',alignItems:'center',gap:8
                          }}>
                            <div style={{fontSize:18,fontWeight:700,color:c.color,letterSpacing:'-.5px'}}>+{c.n}</div>
                            <div style={{fontSize:11,color:'var(--tx2)',fontWeight:500}}>{c.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                const eid = e.id || `${e.at}-${i}`;
                const isExpanded = expandedEntry === eid;
                const hasDetails = e.details && Object.keys(e.details).length > 0;
                return (
                  <div key={eid}>
                    <div onClick={() => hasDetails && setExpandedEntry(isExpanded ? null : eid)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 18px',
                        borderTop: '1px solid var(--bds)',
                        cursor: hasDetails ? 'pointer' : 'default',
                        background: isExpanded ? 'var(--bg)' : 'transparent',
                        transition: 'background .12s'
                      }}>
                      <div style={{ flex: '0 0 28px', height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 9999, background: 'var(--bg)', color: t.color, fontSize: 14 }}>{t.icon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--tx)' }}>{e.summary}</div>
                        <div style={{ fontSize: 10, color: 'var(--tx-mu)', marginTop: 2 }}>
                          {t.label} · {fmtTime(e.at)}
                          {hasDetails && <span style={{marginLeft:8,color:'var(--pu)',fontWeight:600}}>{isExpanded?'▲ ocultar detalle':'▼ ver detalle'}</span>}
                        </div>
                      </div>
                      {hsUrl && <a href={hsUrl} target="_blank" rel="noreferrer" onClick={ev=>ev.stopPropagation()}
                        style={{ alignSelf:'center', color: 'var(--bl)', fontSize: 11, fontWeight: 600, textDecoration: 'none', whiteSpace:'nowrap' }}>
                        HubSpot →
                      </a>}
                    </div>
                    {isExpanded && hasDetails && (
                      <div style={{
                        background:'var(--bg)', padding:'10px 18px 14px',
                        borderTop:'1px dashed var(--bds)',
                        fontFamily:'monospace', fontSize:11, lineHeight:1.5,
                        color:'var(--tx2)', whiteSpace:'pre-wrap', wordBreak:'break-word'
                      }}>
                        {JSON.stringify(e.details, null, 2)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
}
